/**
 * Filtro specializzato per valori numerici
 *
 * @description
 * NumberFilter è ottimizzato per la gestione di valori numerici e fornisce
 * validazione automatica, conversione da stringhe URL e gestione di valori null/undefined.
 * Supporta tutti gli operatori di comparazione numerica (>, <, >=, <=, =, !=).
 */

import { BaseFilter } from '../core/base-filter';
import type { QueryParams } from '../types';

/**
 * Interfaccia per dati di filtro numerico
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 */
export interface INumberFilterData {
	/** Nome della proprietà da filtrare */
	propertyName: string;
	/** Valore corrente del filtro */
	value: number | null;
	/** Valore di default del filtro */
	defaultValue: number | null;
	/** Nome della proprietà nella query string */
	queryPropertyName: string;
	/** Operatore di ricerca applicato */
	searchOperator: string;
	/** Se aggiornare la query string al cambio */
	updateQueryOnChange: boolean;
}

/**
 * Tipo combinato per filtro numerico con metodi fluenti
 */
export type NumberFilterFactory = INumberFilterData & {
	/** Imposta il valore iniziale */
	initialValue(value: number | null): NumberFilterFactory;
	/** Imposta il valore di default */
	default(value: number | null): NumberFilterFactory;
	/** Configura aggiornamento query string */
	updateQueryOnFilterChange(value: boolean): NumberFilterFactory;
	/** Configura nome proprietà query string */
	setQueryPropertyName(value: string): NumberFilterFactory;
	/** Operatore "uguale" */
	equals(): NumberFilterFactory;
	/** Operatore "maggiore di" */
	greaterThan(): NumberFilterFactory;
	/** Operatore "maggiore o uguale" */
	greaterThanOrEqual(): NumberFilterFactory;
	/** Operatore "minore di" */
	lessThan(): NumberFilterFactory;
	/** Operatore "minore o uguale" */
	lessThanOrEqual(): NumberFilterFactory;
	/** Operatore "diverso da" */
	notEquals(): NumberFilterFactory;
};

/**
 * Filtro per valori numerici
 *
 * @example
 * // Filtro per ID cliente
 * const customerIdFilter = new NumberFilter('customerId')
 *   .equals()           // Operatore di uguaglianza
 *   .initialValue(123); // Valore iniziale
 *
 * @example
 * // Filtro per prezzo minimo
 * const minPriceFilter = new NumberFilter('price')
 *   .greaterThanOrEqual()  // Prezzo >= valore
 *   .default(0)            // Default 0 (non appare nell'URL)
 *   .updateQueryOnFilterChange(true);
 *
 * @example
 * // Filtro per quantità in range
 * const quantityFilter = new NumberFilter('quantity')
 *   .between()          // Tra due valori (richiede array [min, max])
 *   .queryPropertyName('qty');  // Nome personalizzato nell'URL
 *
 * @example
 * // Filtro per ID in array
 * const idsFilter = new NumberFilter('categoryId')
 *   .in()              // WHERE categoryId IN (1,2,3,...)
 *   .initialValue([1, 2, 3]);
 *
 * @category Filters
 * @since 1.0.0
 */
export class NumberFilter extends BaseFilter<number | null> {
	/**
	 * Crea una nuova istanza di NumberFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare (supporta notazione con punto: 'order.customerId')
	 *
	 * @example
	 * // Filtro semplice per ID
	 * const filter = new NumberFilter('id');
	 *
	 * @example
	 * // Filtro per proprietà nidificata
	 * const filter = new NumberFilter('order.customer.id');
	 *
	 * @public
	 */
	constructor(propertyName: string) {
		super(Number, propertyName);
	}

	/**
	 * Deserializza il valore dalla query string
	 * Converte stringhe in numeri con validazione automatica
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?customer_id=123
	 * filter._fromQuery({ customer_id: '123' });
	 * // filter.value diventa 123 (number)
	 *
	 * @example
	 * // URL: ?customer_id=abc (valore non valido)
	 * filter._fromQuery({ customer_id: 'abc' });
	 * // filter.value diventa null (o defaultValue se impostato)
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined && value !== this.value) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			// Gestisce conversione sicura da stringa a numero
			if (value === null || value === '' || value === undefined) {
				this.value = this._defaultValue || null;
			} else {
				const numericValue = Number(value);
				// Verifica che sia un numero valido (non NaN)
				this.value = !isNaN(numericValue) ? numericValue : this._defaultValue || null;
			}
		}
	}

	/**
	 * Serializza il valore per la query string
	 * Converte il numero in stringa per l'URL
	 *
	 * @returns Il valore numerico come stringa
	 *
	 * @example
	 * filter.value = 123;
	 * filter._getSerializedValue(); // '123'
	 *
	 * @example
	 * filter.value = null;
	 * filter._getSerializedValue(); // undefined (non appare nell'URL)
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		return this.value !== null && this.value !== undefined ? String(this.value) : undefined;
	}

	/**
	 * Verifica se il valore è quello di default
	 * Considera anche null e 0 come possibili valori di default
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @example
	 * filter._defaultValue = 0;
	 * filter.value = 0;
	 * filter._valueIsDefault(); // true
	 *
	 * filter.value = 5;
	 * filter._valueIsDefault(); // false
	 *
	 * @protected
	 * @override
	 */
	protected _valueIsDefault(): boolean {
		return this.value === undefined || this.value === null || this.value === this._defaultValue;
	}
}

/**
 * Filtro specializzato per range numerici
 *
 * @description
 * NumberRangeFilter gestisce array di numeri per operazioni di range (between, notBetween)
 * o per operazioni IN con più valori. Serializza gli array come stringhe separate da '*'.
 *
 * @example
 * // Filtro per range di prezzi
 * const priceRangeFilter = new NumberRangeFilter('price')
 *   .between()                    // WHERE price BETWEEN min AND max
 *   .initialValue([100, 500]);    // Da 100 a 500
 *
 * @example
 * // Filtro per multiple categorie
 * const categoriesFilter = new NumberRangeFilter('categoryId')
 *   .in()                        // WHERE categoryId IN (1,2,3)
 *   .initialValue([1, 2, 3]);
 *
 * @category Filters
 * @since 1.0.0
 */
export class NumberRangeFilter extends BaseFilter<number[] | null> {
	/**
	 * Crea una nuova istanza di NumberRangeFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare
	 *
	 * @example
	 * const filter = new NumberRangeFilter('price');
	 *
	 * @public
	 */
	constructor(propertyName: string) {
		super(Array, propertyName);
	}

	/**
	 * Deserializza il valore dalla query string
	 * Converte stringhe separate da '*' in array di numeri
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?price_range=100*500
	 * filter._fromQuery({ price_range: '100*500' });
	 * // filter.value diventa [100, 500]
	 *
	 * @example
	 * // URL: ?categories=1*2*3*4
	 * filter._fromQuery({ categories: '1*2*3*4' });
	 * // filter.value diventa [1, 2, 3, 4]
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			if (!value) {
				this.value = this._defaultValue || null;
			} else {
				// Gestisce sia stringhe che array dalla query
				const stringValue = String(value);
				const tokens = stringValue
					.split('*')
					.map((item) => {
						const num = Number(item.trim());
						return !isNaN(num) ? num : 0;
					})
					.filter((num) => num !== 0 || stringValue.includes('0'));

				this.value = tokens.length > 0 ? tokens : this._defaultValue || null;
			}
		}
	}

	/**
	 * Serializza l'array di numeri per la query string
	 * Converte l'array in stringa con separatore '*'
	 *
	 * @returns Array serializzato come stringa
	 *
	 * @example
	 * filter.value = [100, 500];
	 * filter._getSerializedValue(); // '100*500'
	 *
	 * @example
	 * filter.value = [1, 2, 3];
	 * filter._getSerializedValue(); // '1*2*3'
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		if (this.value && Array.isArray(this.value) && this.value.length > 0) {
			return this.value.join('*');
		}
		return undefined;
	}

	/**
	 * Verifica se il valore è quello di default
	 * Considera array vuoti come valori di default
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @example
	 * filter.value = [];
	 * filter._valueIsDefault(); // true
	 *
	 * filter.value = [1, 2];
	 * filter._valueIsDefault(); // false
	 *
	 * @protected
	 * @override
	 */
	protected _valueIsDefault(): boolean {
		return (
			this.value === undefined ||
			this.value === null ||
			(Array.isArray(this.value) && this.value.length === 0) ||
			this.value === this._defaultValue
		);
	}

	/**
	 * Override del metodo _setContext per gestire array vuoti
	 * Non aggiunge il filtro al contesto se l'array è vuoto
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @protected
	 * @override
	 */
	protected _setContext(context: any): void {
		// Non aggiunge il filtro se l'array è vuoto o null
		if (!this.value || !Array.isArray(this.value) || this.value.length === 0) {
			return;
		}

		// Delega alla implementazione della classe base
		super._setContext(context);
	}
}

/**
 * Factory function per creare filtri numerici tipizzati
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @param propertyName - Nome della proprietà da filtrare
 * @returns Oggetto filtro numerico tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API
 * const customerIdFilter = ref(createNumberFilterData('customerId').equals());
 *
 * @example
 * // Con configurazione completa
 * const priceFilter = ref(
 *   createNumberFilterData('price')
 *     .greaterThanOrEqual()
 *     .initialValue(100)
 *     .updateQueryOnFilterChange(true)
 * );
 */
export function createNumberFilterData(propertyName: string): NumberFilterFactory {
	// Sanitizza nome proprietà per query string
	const sanitizedName = propertyName.replace(/[.[\\]]/g, '_').toLowerCase();

	// Oggetto combinato con dati e metodi
	const filter: NumberFilterFactory = {
		propertyName,
		value: null,
		defaultValue: null,
		queryPropertyName: sanitizedName,
		searchOperator: 'NOOP',
		updateQueryOnChange: false,

		initialValue(value: number | null): NumberFilterFactory {
			filter.value = value;
			return filter;
		},

		default(value: number | null): NumberFilterFactory {
			filter.defaultValue = value;
			return filter;
		},

		updateQueryOnFilterChange(value: boolean): NumberFilterFactory {
			filter.updateQueryOnChange = value;
			return filter;
		},

		setQueryPropertyName(value: string): NumberFilterFactory {
			filter.queryPropertyName = value;
			return filter;
		},

		equals(): NumberFilterFactory {
			filter.searchOperator = 'EQUAL';
			return filter;
		},

		greaterThan(): NumberFilterFactory {
			filter.searchOperator = 'GREATER_THAN';
			return filter;
		},

		greaterThanOrEqual(): NumberFilterFactory {
			filter.searchOperator = 'GREATER_THAN_OR_EQUAL';
			return filter;
		},

		lessThan(): NumberFilterFactory {
			filter.searchOperator = 'LESS_THAN';
			return filter;
		},

		lessThanOrEqual(): NumberFilterFactory {
			filter.searchOperator = 'LESS_THAN_OR_EQUAL';
			return filter;
		},

		notEquals(): NumberFilterFactory {
			filter.searchOperator = 'NOT_EQUAL';
			return filter;
		},
	};

	return filter;
}
